<template>
  <div class="kobe-html-report">
    <report-login-modal
      v-if="showLogin"
      :value="showLogin"
      :campaignID="campaignID"
      @loggedIn="onLoggedIn"
      @input="() => {}"
    />
    <div v-if="loading" class="state">Generating report…</div>
    <div v-else-if="error" class="state error">{{ error }}</div>
    <iframe
      v-else-if="html"
      ref="frame"
      class="report-frame"
      :srcdoc="html"
      frameborder="0"
    ></iframe>
  </div>
</template>

<script>
import campaignAPIs from '../views/Campaign/campaignAPIs'
import buildReportHtml, { buildAnalysisContext } from './kobe/buildReportHtml'
import ReportLoginModal from './ReportLoginModal'

/**
 * Kobe-styled campaign HTML report — a NEW standalone route
 * (/reports/campaign/html?id=...), sibling to /reports/campaign/summary which
 * it does not touch. Free-access + passcode-gated (same client-facing flow as
 * the summary report), so the link is shareable but not public. Renders the IMS
 * report data in the Kobe editorial design inside an isolated iframe.
 *
 * The per-platform tables honour the campaign's own column show/hide + order
 * config (campaign_details.<type>AnalysisStatus). Not editable here — editing
 * lives in Campaign Report Internal.
 */
export default {
  name: 'CampaignHtmlReport',
  components: { ReportLoginModal },
  data () {
    return {
      loading: true,
      error: '',
      html: '',
      showLogin: false,
      campaignID: ''
    }
  },
  created () {
    this.campaignID = this.$route.query.id || ''
    if (!this.campaignID) {
      this.error = 'Missing campaign id (use ?id=<campaignID>).'
      this.loading = false
      return
    }
    // passcode can come from the shared link (?passcode=), from a previous
    // login (localStorage), or from the login modal
    const fromUrl = this.$route.query.passcode || ''
    const stored = fromUrl || this.storedPasscode(this.campaignID)
    if (stored) {
      if (fromUrl) this.savePasscode(this.campaignID, fromUrl)
      this.fetchReport(stored)
    } else {
      this.showLogin = true
      this.loading = false
    }
  },
  methods: {
    storedPasscode (campaignID) {
      try {
        const raw = window.localStorage.getItem('reportauth') || 'null'
        const map = raw !== 'null' ? JSON.parse(raw) : {}
        return map && map[campaignID] ? map[campaignID] : ''
      } catch (e) {
        return ''
      }
    },
    savePasscode (campaignID, passcode) {
      try {
        const raw = window.localStorage.getItem('reportauth') || 'null'
        const map = raw !== 'null' ? JSON.parse(raw) : {}
        map[campaignID] = passcode
        window.localStorage.setItem('reportauth', JSON.stringify(map))
      } catch (e) { /* ignore */ }
    },
    // ReportLoginModal validates the passcode and emits it on success
    onLoggedIn (passcode) {
      this.showLogin = false
      this.savePasscode(this.campaignID, passcode)
      this.fetchReport(passcode)
    },
    async fetchReport (passcode) {
      this.loading = true
      this.error = ''
      try {
        const body = await campaignAPIs.generateCampaignHTMLReport(this.campaignID, passcode, null, false, 'json')
        const data = body && body.success ? body.data : (body && body.data ? body.data : body)
        if (!data || !data.summary) {
          this.error = 'No report data for this campaign yet.'
        } else {
          // Prefer the CURATED analysis saved in Campaign Report Internal
          // (campaign.AISummary); only generate on the fly when none exists.
          const saved = (data.campaign_details || {}).AISummary
          if (saved && String(saved).trim()) {
            this.html = buildReportHtml(data, { aiAnalysis: saved })
          } else {
            // render the report immediately, then fold in the AI analysis when ready
            this.html = buildReportHtml(data)
            this.loadAiAnalysis(data, passcode)
          }
        }
      } catch (e) {
        // stale/invalid passcode -> re-prompt
        this.showLogin = true
        // eslint-disable-next-line no-console
        console.error('CampaignHtmlReport', e)
      } finally {
        this.loading = false
      }
    },
    // AI analysis (Claude via Bedrock) — best-effort; the report still stands
    // without it, so any failure is swallowed and the section is just omitted.
    async loadAiAnalysis (data, passcode) {
      try {
        const context = buildAnalysisContext(data)
        const resp = await campaignAPIs.getHTMLReportAISummary(this.campaignID, passcode, { campaignContext: JSON.stringify(context) })
        const analysis = resp && resp.analysis
        if (analysis) {
          this.html = buildReportHtml(data, { aiAnalysis: analysis })
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('AI analysis unavailable', e && e.message)
      }
    }
  }
}
</script>

<style scoped>
.kobe-html-report { position: fixed; inset: 0; background: #0A0A0A; }
.report-frame { width: 100%; height: 100%; border: 0; display: block; }
.state {
  color: #FAFAFA; background: #0A0A0A; height: 100%;
  display: flex; align-items: center; justify-content: center;
  font-family: 'JetBrains Mono', monospace; font-size: 14px;
}
.state.error { color: #FF3D3D; }
</style>



// WEBPACK FOOTER //
// HtmlReport.vue?75d1fa2c